<template>
  <transition name="operate-modal-fade">
    <div v-if="modelValue" class="operate-modal-mask" @click.self="close">
      <div class="operate-modal-panel">
        <div class="operate-modal-title">{{ t("operateModal.title") }}</div>
        <div class="operate-modal-list">
          <button class="operate-btn" @click="onCopy">
            <img src="@/assets/copy-link.png" class="operate-btn-icon" />
            <span class="operate-btn-text">{{
              t("operateModal.copyLink")
            }}</span>
          </button>
          <button
            v-if="languageStore.showShare"
            class="operate-btn"
            @click="onShareToGame"
          >
            <img src="@/assets/share.png" class="operate-btn-icon" />
            <span class="operate-btn-text">{{
              t("operateModal.shareToGame")
            }}</span>
          </button>
          <button
            v-if="props.showDelete !== false"
            class="operate-btn"
            @click="onDelete"
          >
            <img src="@/assets/delete-icon.png" class="operate-btn-icon" />
            <span class="operate-btn-text">{{ t("operateModal.delete") }}</span>
          </button>
        </div>
        <button class="operate-cancel-btn" @click="close">
          {{ t("common.cancel") }}
        </button>
      </div>
    </div>
  </transition>
  <ShareModal v-model="showShareModal" :thread-info="shareThreadInfo" />
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";
import {
  useThreadStore,
  useToastStore,
  useConfirmStore,
  useUserStore,
  useLanguageStore,
} from "@/stores";
import { useRouter } from "vue-router";
import { clickButton, pageType } from "@/utils/point";
import useClipboard from "vue-clipboard3";
import { GAME_ID } from "@/stores";
import ShareModal from "./ShareModal.vue";

const threadStore = useThreadStore();
const toastStore = useToastStore();
const confirmStore = useConfirmStore();
const userStore = useUserStore();
const languageStore = useLanguageStore();
const props = defineProps<{
  modelValue: boolean;
  shareLink?: string;
  showDelete?: boolean;
  replyId?: number;
}>();
const emits = defineEmits(["update:modelValue", "delete"]);
const close = () => emits("update:modelValue", false);

const showShareModal = ref(false);

const shareThreadInfo = computed(() => {
  const thread = threadStore.currentThread;
  if (!thread?.tid) return null;
  const threadUrl =
    window.globalGetShareLink({ gameId: GAME_ID }) + window.location.hash;
  return {
    tid: thread.tid,
    thread_title: thread.subject ?? "",
    thread_url: threadUrl,
    description: (thread.message ?? "").slice(0, 200),
  };
});

const onShareToGame = () => {
  if (!userStore.checkBind()) {
    return;
  }
  if (!threadStore.currentThread?.tid) {
    toastStore.showToast(t("shareModal.noThreadTip"), "error");
    return;
  }
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "点击分享到游戏",
    eventValue: JSON.stringify({
      tid: threadStore.currentThread.tid,
    }),
  });
  close();
  showShareModal.value = true;
};

const { t } = useI18n();
const router = useRouter();
const { toClipboard } = useClipboard();

const onCopy = async () => {
  try {
    const link =
      window.globalGetShareLink({
        gameId: GAME_ID,
      }) + window.location.hash;
    await toClipboard(link);
    toastStore.showToast(t("operateModal.copySuccess"), "success");
    clickButton({
      eventType: pageType.POST_DETAIL,
      eventReason: "复制链接",
      eventValue: JSON.stringify({
        tid: threadStore.currentThread?.tid,
      }),
    });
    emits("update:modelValue", false);
  } catch (e) {
    toastStore.showToast(t("operateModal.copyFailed"), "error");
  }
};

const onDelete = async () => {
  // 使用自定义确认弹窗
  confirmStore.showConfirm(
    "",
    t("operateModal.confirmDelete"),
    t("operateModal.delete"),
    t("common.cancel"),
    async () => {
      let success = false;
      if (props.replyId) {
        await threadStore.deleteReply(props.replyId);
        clickButton({
          eventType: pageType.POST_DETAIL,
          eventReason: "删除评论",
          eventValue: JSON.stringify({
            pid: props.replyId,
          }),
        });
        success = true;
      } else if (threadStore.currentThreadId) {
        await threadStore.deleteThread(threadStore.currentThreadId);
        clickButton({
          eventType: pageType.POST_DETAIL,
          eventReason: "删除帖子",
          eventValue: JSON.stringify({
            tid: threadStore.currentThreadId,
          }),
        });
        success = true;
      }
      if (success) {
        toastStore.showToast(t("operateModal.deleteSuccess"), "success");
        close();
        emits("delete");
        // 只有在删除帖子时才跳转到首页，删除回复时不需要跳转
        if (!props.replyId) {
          router.push("/");
        }
      } else {
        toastStore.showToast(t("operateModal.deleteFailed"), "error");
      }
    }
  );
};
</script>

<style scoped lang="scss">
.operate-modal-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.operate-modal-panel {
  width: 100vw;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 18px 20px 12px;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.08);
  animation: slideUp 0.25s;
}
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
.operate-modal-title {
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 18px;
}
.operate-modal-list {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}
.operate-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
}
.operate-btn-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  background: #f7f7f7;
  padding: 8px;
  border-radius: 12px;
  object-fit: contain;
}
.operate-btn-text {
  font-size: 15px;
  color: #222;
}
.operate-cancel-btn {
  width: 100%;
  background: #f7f7f7;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  color: #222;
  padding: 14px 0;
  margin-top: 8px;
  cursor: pointer;
}
.operate-modal-fade-enter-active,
.operate-modal-fade-leave-active {
  transition: opacity 0.2s;
}
.operate-modal-fade-enter-from,
.operate-modal-fade-leave-to {
  opacity: 0;
}
</style>
